import React, {ReactNode, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {ThemeProvider} from '@rneui/themed';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import rewardsTheme from '../../../theme/rewardsTheme';

import SignUpEmailScreenContent from './SignUpEmailScreenContent';

/**
 * The screen picks its layout from the width it is given, so the frame is what
 * selects mobile or desktop. `Mobile` pins the Figma 375x812 device frame;
 * `Desktop` fills the preview pane, which is wider than the 768px breakpoint.
 *
 * Every story renders through its own `render` and takes no story args, because
 * the in-app browser at `app/screens/StorybookScreen.web.tsx` calls
 * `story.render()` with no arguments and `decorator(Story)` with no context.
 */
const MobileFrame = ({children}: {children: ReactNode}) => (
  <View style={styles.mobileFrame}>{children}</View>
);

const DesktopFrame = ({children}: {children: ReactNode}) => (
  <View style={styles.desktopFrame}>{children}</View>
);

const noop = () => {};
const logNext = () => console.log('Next pressed');
const logClose = () => console.log('Close pressed');

const meta = {
  title: 'Screens/Sign Up/Email',
  component: SignUpEmailScreenContent,
  decorators: [
    Story => (
      <ThemeProvider theme={rewardsTheme}>
        <Story />
      </ThemeProvider>
    ),
  ],
  parameters: {
    layout: 'fullscreen',
    docs: {
      description: {
        component:
          'Sign-up Screen 1: email entry. Presentational only — no navigation, no store, no API.\n\n' +
          '**Design annotations applied:**\n' +
          '- The brand header in the Figma frame is NOT rendered; only the close icon remains.\n' +
          '- Heading and body copy are centred; the Email label stays left-aligned.\n' +
          '- The hero banner has two crops: 1372x540 under 768px, 2000x279 at or above it.\n' +
          '- At or above 768px the field and NEXT sit side by side in one bottom-aligned row.\n' +
          '- NEXT is disabled until the email parses against the shared `emailSchema`.\n' +
          '- There is no Reset Password link on this page.',
      },
    },
  },
  args: {
    email: '',
    onChangeEmail: noop,
    onNext: logNext,
    onClose: logClose,
  },
} satisfies Meta<typeof SignUpEmailScreenContent>;

export default meta;

type Story = StoryObj<typeof meta>;

/** Empty field on the mobile layout: the NEXT button is disabled. */
export const MobileCtaDisabled: Story = {
  name: 'Mobile — CTA disabled (empty email)',
  render: () => (
    <MobileFrame>
      <SignUpEmailScreenContent email="" onChangeEmail={noop} onNext={logNext} onClose={logClose} />
    </MobileFrame>
  ),
};

/** Partial input that fails `emailSchema`: the NEXT button stays disabled. */
export const MobileCtaDisabledInvalid: Story = {
  name: 'Mobile — CTA disabled (invalid email)',
  render: () => (
    <MobileFrame>
      <SignUpEmailScreenContent
        email="keith@"
        onChangeEmail={noop}
        onNext={logNext}
        onClose={logClose}
      />
    </MobileFrame>
  ),
};

/** Valid email: the NEXT button is enabled and uses the brand primary colour. */
export const MobileCtaEnabled: Story = {
  name: 'Mobile — CTA enabled (valid email)',
  render: () => (
    <MobileFrame>
      <SignUpEmailScreenContent
        email="keith@example.com"
        onChangeEmail={noop}
        onNext={logNext}
        onClose={logClose}
      />
    </MobileFrame>
  ),
};

/** Typing in the field toggles the CTA between its two states. */
export const MobileInteractive: Story = {
  name: 'Mobile — Interactive (type to enable)',
  render: () => {
    const [email, setEmail] = useState('');

    return (
      <MobileFrame>
        <SignUpEmailScreenContent
          email={email}
          onChangeEmail={setEmail}
          onNext={logNext}
          onClose={logClose}
        />
      </MobileFrame>
    );
  },
};

/** Empty field on the desktop layout: wide hero, field and CTA side by side. */
export const DesktopCtaDisabled: Story = {
  name: 'Desktop — CTA disabled (empty email)',
  render: () => (
    <DesktopFrame>
      <SignUpEmailScreenContent email="" onChangeEmail={noop} onNext={logNext} onClose={logClose} />
    </DesktopFrame>
  ),
};

/** Valid email on the desktop layout: the NEXT button is enabled. */
export const DesktopCtaEnabled: Story = {
  name: 'Desktop — CTA enabled (valid email)',
  render: () => (
    <DesktopFrame>
      <SignUpEmailScreenContent
        email="johnsmith@gmail.com"
        onChangeEmail={noop}
        onNext={logNext}
        onClose={logClose}
      />
    </DesktopFrame>
  ),
};

/** Typing in the field toggles the CTA on the desktop layout. */
export const DesktopInteractive: Story = {
  name: 'Desktop — Interactive (type to enable)',
  render: () => {
    const [email, setEmail] = useState('');

    return (
      <DesktopFrame>
        <SignUpEmailScreenContent
          email={email}
          onChangeEmail={setEmail}
          onNext={logNext}
          onClose={logClose}
        />
      </DesktopFrame>
    );
  },
};

const styles = StyleSheet.create({
  mobileFrame: {
    width: 375,
    height: 812,
    alignSelf: 'center',
    backgroundColor: '#ffffff',
  },
  desktopFrame: {
    width: '100%',
    minHeight: 700,
    backgroundColor: '#ffffff',
  },
});
